import { IMessageDTO as IPostedMessageDTO } from "./InteractionDTO";
import { IPaginatedDTO } from "./PaginatedDTO";

export enum MessageFunctionOutcome {
  SUCCESS = "success",
  ERROR = "error",
  NOT_EXECUTED = "not-executed",
  INCOMPLETE = "incomplete"
}

interface IMessageFunction {
  type: string;
  outcome?: MessageFunctionOutcome;
}

interface IChangeAgentFunction extends IMessageFunction {
  agentName: string;
  type: string;
  reason?: string;
}

interface IUpdatePreferencesFunction extends IMessageFunction {
  details: Record<string, any>;
  type: string;
}

interface IStartApplicationFunction extends IMessageFunction {
  candidateId: string;
  type: string;
}

export enum Agent {
  ALIGNMENT = "alignment",
  HUMAN = "human",
  SUMMARIZER = "summarizer",
  DYNAMIC = "dynamic",
  ORCHESTRATOR_EMAIL = "orchestrator-email",
  ORCHESTRATOR_MESSENGER = "orchestrator-messenger",
  REQUEST_SCREENER = "request-screener",
  LANGUAGE_DETECTOR = "language-detector",
  VERIFIER = "verifier",
  PLANNER = "planner",
  PRE_SCREENER = "pre-screener",
  SKILL_MAPPER = "skill-mapper",
  RELEVANCE = "relevance",
  INTERMEDIATE_MESSENGER = "intermediate-messenger",
  FAQ = "faq",
  MARKETING = "marketing",
  JOB_POSTING_EXTRACTOR = "job-posting-extractor",
  JOB_POSTING = "job-posting",
  BUG_TRIAGE = "bug-triage",
  APPLICATION = "application",
  EXPERIENCE_MANAGEMENT = "experience-management",
  INTRO = "intro",
  JOB_POSTING_NEXT_OPENING = "job-posting-next-opening",
  RECEPTIONIST = "receptionist"
}

export interface ITokensUsedDTO {
  inputTokens: number;
  outputTokens: number;
}

export enum ThreadEnvironment {
  TEST = "test",
  PRODUCTION = "production"
}

export interface IAgentConfigurationDTO {
  disabled: boolean,
  disabledFunctions: string[],
  name: Agent,
  promptVersion?: number
};

export interface IThreadConfigurationDTO {
  agents: IAgentConfigurationDTO[],
  environment: ThreadEnvironment
};

export type EmmaFunction = IChangeAgentFunction | IUpdatePreferencesFunction | IStartApplicationFunction

export interface IThreadDTO {
  id: number;
  personGgId: string;
  agent: Agent;
  isBanned: boolean;
  configuration: IThreadConfigurationDTO;
}

export interface IUpdateThreadDTO {
  agent?: Agent;
  isBanned?: boolean;
}

export interface IMessageDTO {
  id: number;
  threadId: number;
  messageRefId: string;
  source: string
  agent?: Agent;
  promptId?: number;
  cost: number;
  userMessage: string;
  assistantResponse?: string;
  tokensUsed?: ITokensUsedDTO;
  thread?: IThreadDTO;
  functions: EmmaFunction[];
  hasEvaluation: boolean;
  created: string;
}

export interface IFilterMessageDTO {
  messageRefIds?: string[];
  messageSources?: string[];
  userGgIds?: string[];
  messageIds?: number[];
  agents?: Agent[];
  messageStatuses?: string[];
  steps?: string[];
  excludeEvaluated?: boolean;
  threadEnvironment?: string;
  after?: string;
  sort?: "asc" | "desc";
};

export class PaginatedMessageDTO implements IPaginatedDTO<IMessageDTO> {
  page: number = 1;
  total: number;
  elements: IMessageDTO[];
  pageSize: number;
  pageLength: number;

  constructor(data: IPaginatedDTO<IMessageDTO>) {
    this.page = data.page || 1;
    this.total = data.total;
    this.elements = data.elements;
    this.pageSize = data.elements.length || 10;
    this.pageLength = Math.ceil(data.total / this.pageSize);
  };
};

export enum CaseIdentifiers {
  ACTIVE_SEEKING = "active-seeking",
  NO_PREVIOUS_INTERACTION = "no-previous-interaction",
  PASSIVE_SEEKING = "passive-seeking",
  HAS_JOB = "has-job",
  NO_SEEKING_JOB = "no-seeking-job",
  DONT_REACH_OUT = "dont-reach-out",
  BUG_REPORTED = "bug-reported",
  INTERESTED_TO_APPLY = "interested-to-apply",
  NEXT_STEPS_INFO = "next-steps-info",
  ALREADY_APPLIED = "already-applied",
  UNINTERESTED_TO_APPLY = "uninterested-to-apply",
  NOT_ALIGNED_JOB = "not-aligned-job",
  OTHER_JOBS = "other-jobs",
  JOB_INFO = "job-info",
  QUESTIONS_ABOUT_SQ_LINK = "questions-about-sq-link",
  WILL_DO = "will-do",
  SCREENING_QUESTIONS_DONE = "screening-questions-done",
  CONTACT_COMPANY_THEMSELVES = "contact-company-themselves",
  MEETING_REQUEST = "meeting-request",
  AUTO_MESSAGE_RECEIVED = "auto-message-received",
  INTEREST_JOB_TYPE = "interest-job-type",
  SKILLS_INFO = "skills-info",
  LANG_FLUENCY = "lang-fluency",
  JOB_COMPENSATION = "job-compensation",
  USER_COMPENSATION_INFO = "user-compensation-info",
  USER_LOCATION_INFO = "user-location-info",
  JOB_FEEDBACK = "job-feedback",
  OPT_IN_WHATSAPP_NO = "opt-in-wa-no",
  OPT_IN_WHATSAPP_YES = "opt-in-wa-yes",
  POTENTIAL_PARTNERSHIP = "potential-partnership",
  PROFILE_COMPLETED_DONE = "profile-completed-done",
  REFERRING = "referring",
  REMOTE_WORK_YES = "remote-work-yes",
  SORRY_FOR_LATE_REPLY = "sorry-for-late-reply",
  THANKS_ACK = "thanks-ack",
  QUESTION_ABOUT_EMMA = "question-about-emma",
  GREETING = "greeting",
  USER_TALENT_SEEKER = "user-talent-seeker",
  WHY_CONTACT = "why-contact",
  CONTACT_INFORMATION_PROVIDED = "contact-information-provided",
  WAITING_FOR_RESPONSE = "waiting-for-response",
  TALK_ABOUT_THEM = "talk-about-them",
  ASK_ADVICE = "ask-advice",
  UNHAPPY_USER = "unhappy-user",
  REMOTE_WORK_NO = "remote-work-no",
  QUESTION_APPLICATION = "question-application",
  ANOTHER_CHANNEL = "another-channel",
  REQUEST_JOB_SUGGESTIONS = "request-job-suggestions",
  VERIFIER_PROVIDED = "verifier-provided",
  ACCOUNT_DELETION_REQUEST = "account-deletion-request",
  CANDIDATE_JOB_POST_INQUIRY = "candidate-job-post-inquiry",
  LOW_APPLICANTS_NUMBER = "low-applicants-number",
  SCAM_REPORTED = "scam-reported",
  TUTORIAL_REQUESTED = "tutorial-requested",
  PRE_SCREENING = "pre-screening",
  PRE_SCREENING_INTRO = "pre-screening-intro",
  PRE_SCREENING_COMPENSATION_MATCH_FACTOR = "pre-screening-compensation-match-factor",
  PRE_SCREENING_FILTERING_SCREENING_QUESTIONS = "pre-screening-filtering-screening-questions",
  PRE_SCREENING_SCREENING_QUESTIONS = "pre-screening-screening-questions",
  PRE_SCREENING_PERSONAL_ID = "pre-screening-personal-id",
  PRE_SCREENING_LOCATION_MATCH_FACTOR = "pre-screening-location-match-factor",
  PRE_SCREENING_LANGUAGE_MATCH_FACTOR = "pre-screening-language-match-factor",
  PRE_SCREENING_SKILL_MATCH_FACTOR = "pre-screening-skill-match-factor",
  PRE_SCREENING_RELEVANCE_MATCH_FACTOR = "pre-screening-relevance-match-factor",
  PRE_SCREENING_COMPLETED = "pre-screening-completed",
  USER_SENT_ATTACHMENT = "user-sent-attachment",
  TOPIC_SELECTED = "topic-selected",
  PROFILE_CV_SUBMITTED = "profile-cv-submitted",
  PROFILE_LINKEDIN_SUBMITTED = "profile-linkedin-submitted",
  PROFILE_INFORMATION_UNAVAILABLE = "profile-information-unavailable",
  FAQ_ASKED = "faq-asked",
  RETRACT_APPLICATION = "retract-application",
  HAS_PROVISIONAL_NAME = "has-provisional-name",
  MANAGE_JOB_POSTING = "manage-job-posting",
  INTEREST_IN_FINDING_JOB = "interest-in-finding-job",
  POSITIVE_TESTIMONIAL = "positive-testimonial",
  COMPANY_ONLINE_PRESENCE = "company-online-presence",
  POSTER_IDENTITY_VERIFICATION = "poster-identity-verification",
  ABOUT_MEETINGS = "about-meetings",
  ABOUT_PROFESSIONAL_GENOME = "about-professional-genome",
  ABOUT_TORRE_REACH = "about-torre-reach",
  ACCEPT_PITCH_INTRODUCTION = "accept-pitch-introduction",
  ACCOUNT_MANAGEMENT = "account-management",
  ADMIN_MANAGEMENT_INQUIRY = "admin-management-inquiry",
  BEHAVIORAL_TRAITS_QUESTIONNAIRE = "behavioral-traits-questionnaire",
  CANDIDATE_MANAGEMENT_INQUIRY = "candidate-management-inquiry",
  CHANGE_LANGUAGE_INQUIRY = "change-language-inquiry",
  DECLINE_PITCH_INTRODUCTION = "decline-pitch-introduction",
  DISTRIBUTION_REQUEST = "distribution-request",
  EMPLOYMENT_SERVICES_INFORMATION_REQUESTED = "employment-services-information-requested",
  INTEGRATION_INQUIRY = "integration-inquiry",
  JOB_OPENING_MANAGEMENT_INQUIRY = "job-opening-management-inquiry",
  MARKETING_TRANSFER_TO_DYNAMIC = "marketing-transfer-to-dynamic",
  MARKETING_TRANSFER_TO_JOB_POSTING = "marketing-transfer-to-job-posting",
  MARKETING_UNSUBSCRIBE = "marketing-unsubscribe",
  MESSENGER_INQUIRY = "messenger-inquiry",
  PRE_SCREENING_EXPERIENCE_INCOMPLETE = "pre-screening-experience-incomplete",
  PRE_SCREENING_RETRACTED = "pre-screening-retracted",
  PRE_SCREENING_RULED_OUT = "pre-screening-ruled-out",
  PRE_SCREENING_SKILLS_DETAIL_REQUIRED = "pre-screening-skills-detail-required",
  PRE_SCREENING_SKILLS_MATCH_FACTOR = "pre-screening-skills-match-factor",
  PRICING_INQUIRY = "pricing-inquiry",
  QUESTIONS_ABOUT_TORRE_PRIME = "questions-about-torre-prime",
  RECRUITMENT_INFO_PROVIDED = "recruitment-info-provided",
  SHOW_MORE_OPPORTUNITIES = "show-more-opportunities",
  SIGNAL_INQUIRY = "signal-inquiry",
  TEAM_GENOME_INQUIRY = "team-genome-inquiry"
}

export interface IFilterPromptDTO {
  agents?: Agent[];
  caseIdentifiers?: CaseIdentifiers[]
}

export interface IPromptDTO {
  id: number;
  agent?: Agent;
  caseIdentifier?: CaseIdentifiers;
  version: number;
  text: string;
  enabledEnvironments: ThreadEnvironment[];
  tools: string[];
  created: string;
  deactivated?: string
}

export interface IUpdatePromptDTO {
  text?: string;
  enabledEnvironments?: ThreadEnvironment[];
}

export class PaginatedPromptDTO implements IPaginatedDTO<IPromptDTO> {
  page: number = 1;
  total: number;
  elements: IPromptDTO[];
  pageSize: number;
  pageLength: number;

  constructor(data: IPaginatedDTO<IPromptDTO>) {
    this.page = data.page || 1;
    this.total = data.total;
    this.elements = data.elements;
    this.pageSize = data.elements.length || 10;
    this.pageLength = Math.ceil(data.total / data.elements.length);
  };
};

export const defaultThreadConfiguration = (): IThreadConfigurationDTO => ({
  agents: [
    {
      disabled: false,
      disabledFunctions: [],
      name: Agent.DYNAMIC
    },
    {
      disabled: false,
      disabledFunctions: [],
      name: Agent.HUMAN
    }
  ],
  environment: ThreadEnvironment.TEST
});

export interface IMessageWithConfigurationDTO {
  message: IPostedMessageDTO,
  threadConfiguration: IThreadConfigurationDTO
};

export interface IProcessedMessageResultDTO {
  id?: number
};

export enum MessageProcessingStatus {
  COMPLETED = "completed"
}

export interface IProximaFirebaseMessageDTO {
  id: number,
  processingStatus: MessageProcessingStatus
}

export interface ISaveRequestDTO {
  caseIdentifier: string,
  phrase: string,
  topic: string
};

export interface IUpdateRequestDTO {
  caseIdentifier?: string,
  phrase?: string,
  topic?: string
};

export interface IFilterRequestsDTO {
  caseIdentifiers?: CaseIdentifiers[]
};

export interface IRequestDTO {
  id: number,
  caseIdentifier: CaseIdentifiers,
  phrase: string,
  topic: string,
  isEmbedded: boolean,
  created: string
};

export class PaginatedRequestDTO implements IPaginatedDTO<IRequestDTO> {
  page: number = 1;
  total: number;
  elements: IRequestDTO[];
  pageSize: number;
  pageLength: number;

  constructor(data: IPaginatedDTO<IRequestDTO>) {
    this.page = data.page || 1;
    this.total = data.total;
    this.elements = data.elements;
    this.pageSize = data.elements.length || 10;
    this.pageLength = Math.ceil(data.total / data.elements.length);
  };
};
